/**
 * LinkedIn verification checkpoints, shared by the campaign form and the Settings
 * sending-accounts table.
 *
 * Lifted out of CampaignFormPage so the two places cannot disagree about what a
 * checkpoint type means. Getting this wrong is not cosmetic: showing a code field
 * for IN_APP_VALIDATION asks for a code that does not exist, and hiding one for OTP
 * leaves the user with no way to finish connecting.
 */

/** Human-readable instruction per LinkedIn checkpoint type. */
export function checkpointHint(type: string): string {
  switch ((type || '').toUpperCase()) {
    case '2FA':
      return 'Enter the 6-digit code from your authenticator app (Google Authenticator, 1Password…).';
    case 'OTP':
      return 'LinkedIn sent a verification code to your email or phone. Enter it below.';
    case 'IN_APP_VALIDATION':
      // Names the button that is actually there. It said "Continue", which the dialog
      // has not had for a while — so the one instruction on screen pointed at nothing.
      return 'Open the LinkedIn mobile app and approve the sign-in request, then click I\'ve approved — Continue.';
    case 'CAPTCHA':
      return 'LinkedIn is showing a captcha for this account — it cannot be solved here. Remove the account and try again later.';
    case 'PHONE_REGISTER':
      return 'LinkedIn requires a phone number on this account. Add one at linkedin.com, then reconnect.';
    default:
      return 'LinkedIn requires additional verification. Enter the code you received below.';
  }
}

/**
 * The toast shown the moment a connection stops on verification.
 *
 * It said "Enter the code below." for every type — including an in-app approval, where
 * there is no code and no field, so the one instruction on screen sent people looking
 * for something that was not there. For an approval the page now watches for it by
 * itself, and says so.
 */
export function checkpointToast(type?: string | null): string {
  switch (checkpointMode(type || '')) {
    case 'approve':
      return 'Approve the sign-in in your LinkedIn app — this page updates by itself once you do.';
    case 'blocked':
      return 'LinkedIn needs an extra step that cannot be completed here. See the note below.';
    default:
      return 'LinkedIn requires verification. Enter the code below.';
  }
}

/**
 * How long an in-app approval is worth waiting for. Unipile keeps a sign-in attempt —
 * and its checkpoint — alive for five minutes; after that it is gone and no amount of
 * asking will complete it.
 */
export const LINKEDIN_CHECKPOINT_WINDOW_MS = 5 * 60 * 1000;

/**
 * How a checkpoint type is resolved in the UI:
 *   'code'    → user types a verification code (2FA, OTP, unknown types)
 *   'approve' → no code; user approves in the LinkedIn app, then confirms (IN_APP_VALIDATION)
 *   'blocked' → cannot be resolved here; only show guidance (CAPTCHA, PHONE_REGISTER)
 */
export function checkpointMode(type: string): 'code' | 'approve' | 'blocked' {
  switch ((type || '').toUpperCase()) {
    case 'IN_APP_VALIDATION':           return 'approve';
    case 'CAPTCHA':
    case 'PHONE_REGISTER':              return 'blocked';
    default:                            return 'code';
  }
}
